import { Download, Ellipsis, ListChecks, Tag, Trash2 } from 'lucide-react'
import * as React from 'react'
import { toast } from 'sonner'
import { bulkDeleteArtifacts, deleteArtifact, getDownloadUrl } from '@/client/sdk.gen'
import type { ArtifactResponse, ArtifactStatusEnum } from '@/client/types.gen'
import { CircleAlertIcon } from '@/components/icons/lucide-circle-alert'
import { ArtifactEditModal } from '@/components/project/ArtifactEditModal'
import { getLabelDisplayOrNull } from '@/components/project/artifact-labels'
import { getFileIconForArtifact } from '@/components/project/artifact-viewer/fileIcons'
import {
	AlertDialog,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Spinner } from '@/components/ui/spinner'
import { useTranslation } from '@/hooks/useTranslation'
import { cn } from '@/lib/utils'
import {
	DOCUMENT_PIPELINE_PHASE_I18N_KEY,
	type DocumentProcessingPipelinePhase,
} from '@/types/documentProcessingPipeline'
import {
	captureUnexpectedError,
	extractErrorMessage,
	isErrorHandledByInterceptor,
} from '@/utils/error'

const PROCESSING_LONG_MS = 60_000

function processingAnchorStorageKey(artifactId: string): string {
	return `mm-artifact-processing-anchor-${artifactId}`
}

export interface ArtifactTileProps extends React.HTMLAttributes<HTMLDivElement> {
	title: string
	fileType: string
	status: ArtifactStatusEnum
	updatedAt?: string
	projectId: string
	artifactId: string
	onDeleted?: (artifactId: string) => void
	/** Full artifact for edit modal (required for Edit option) */
	artifact?: ArtifactResponse | null
	/** Callback when artifact is updated (artifactId passed when editing from tile) */
	onEditSuccess?: (artifactId: string) => void
	/** Called immediately when delete is confirmed, before the API request (optimistic list removal) */
	onOptimisticRemove?: (artifactId: string) => void
	/** Called when delete API fails after an optimistic remove; parent should reload context */
	onOptimisticRemoveFailed?: (artifactId: string) => void
	/** Called after delete API succeeds when optimistic remove was used (release client-side ignore flags) */
	onOptimisticRemoveComplete?: (artifactId: string) => void
	/** Hide late confirm updates for this upload as soon as the user confirms delete. */
	onMarkUploadCancelled?: (artifactId: string) => void
	/** Stop the in-browser upload after the file was removed. */
	onAbortUploadNetwork?: (artifactId: string) => void
	/** Allow an in-flight upload to confirm again after delete fails. */
	onClearUploadCancelled?: (artifactId: string) => void
	canDeleteByRights?: boolean
	/** Switch page into bulk-delete selection mode (optionally preselect this artifact) */
	onStartBulkDelete?: (artifactId: string) => void
	/** Metadata labels for uploading items (when artifact not yet available) */
	metadataLabels?: { time_dimension?: string; purpose?: string; purpose_custom?: string }
	/** Sub-state while `status` is `processing` (from realtime pipeline webhooks) */
	processingPipelinePhase?: DocumentProcessingPipelinePhase
}

const ArtifactTile = React.forwardRef<HTMLDivElement, ArtifactTileProps>(
	(
		{
			title,
			fileType,
			status,
			updatedAt,
			projectId,
			artifactId,
			onDeleted,
			onOptimisticRemove,
			onOptimisticRemoveFailed,
			onOptimisticRemoveComplete,
			onMarkUploadCancelled,
			onAbortUploadNetwork,
			onClearUploadCancelled,
			artifact,
			onEditSuccess,
			onStartBulkDelete,
			canDeleteByRights = false,
			metadataLabels,
			processingPipelinePhase,
			className,
			...props
		},
		ref
	) => {
		const { t } = useTranslation()
		const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false)
		const [isDeleting, setIsDeleting] = React.useState(false)
		const [isEditModalOpen, setIsEditModalOpen] = React.useState(false)
		const [downloading, setDownloading] = React.useState(false)
		const [showLongProcessingHint, setShowLongProcessingHint] = React.useState(false)

		React.useEffect(() => {
			const key = processingAnchorStorageKey(artifactId)
			if (status !== 'processing') {
				try {
					sessionStorage.removeItem(key)
				} catch {
					// ignore quota / private mode
				}
				setShowLongProcessingHint(false)
				return
			}

			const anchorSource = artifact?.processing_started_at ?? updatedAt

			try {
				const fromApi = anchorSource ? Date.parse(anchorSource) : Number.NaN
				const anchorMs = Number.isFinite(fromApi) ? fromApi : Date.now()
				sessionStorage.setItem(key, String(anchorMs))
				const tick = () => {
					setShowLongProcessingHint(Date.now() - anchorMs >= PROCESSING_LONG_MS)
				}
				tick()
				const id = window.setInterval(tick, 5000)
				return () => window.clearInterval(id)
			} catch {
				const started = Date.now()
				const tick = () => setShowLongProcessingHint(Date.now() - started >= PROCESSING_LONG_MS)
				tick()
				const id = window.setInterval(tick, 5000)
				return () => window.clearInterval(id)
			}
		}, [status, artifactId, updatedAt, artifact?.processing_started_at])

		const getStatusConfig = () => {
			if (!status) return null

			switch (status) {
				case 'pending_upload':
					return {
						text: t('pages.projectDetail.artifactStatus.uploading'),
						icon: Spinner,
						color: 'text-purple-600',
						bgColor: 'bg-purple-50',
						borderColor: 'border-purple-200',
					}
				case 'processing': {
					if (processingPipelinePhase === 'FAILED' || processingPipelinePhase === 'REVOKED') {
						return {
							text: t(DOCUMENT_PIPELINE_PHASE_I18N_KEY[processingPipelinePhase]),
							icon: CircleAlertIcon,
							color: 'text-red-600',
							bgColor: 'bg-red-50',
							borderColor: 'border-red-200',
						}
					}
					const labelKey = processingPipelinePhase
						? DOCUMENT_PIPELINE_PHASE_I18N_KEY[processingPipelinePhase]
						: 'pages.projectDetail.artifactStatus.processing'
					return {
						text: t(labelKey),
						icon: Spinner,
						color: 'text-purple-600',
						bgColor: 'bg-purple-50',
						borderColor: 'border-purple-200',
					}
				}
				case 'completed':
					return {
						text: t('pages.projectDetail.artifactStatus.complete'),
						icon: null,
						color: 'text-gray-600',
						bgColor: 'bg-gray-50',
						borderColor: 'border-gray-200',
					}
				case 'failed':
					return {
						text: t('pages.projectDetail.artifactStatus.failed'),
						icon: CircleAlertIcon,
						color: 'text-red-600',
						bgColor: 'bg-red-50',
						borderColor: 'border-red-200',
					}
				default:
					return {
						text: t('pages.projectDetail.artifactStatus.unknown'),
						icon: Spinner,
						color: 'text-gray-600',
						bgColor: 'bg-gray-50',
						borderColor: 'border-gray-200',
					}
			}
		}

		const statusConfig = getStatusConfig()
		const isCompleted = !status || status === 'completed'
		const isFailed = status === 'failed'
		const isProcessing = status === 'pending_upload' || status === 'processing'
		const canDeleteArtifact = canDeleteByRights
		const showEllipsis = isCompleted || isFailed || isProcessing
		const isSpinning = isProcessing

		const metadataBadges: string[] = []
		if (status !== 'failed') {
			const timeDim = artifact?.time_dimension ?? metadataLabels?.time_dimension
			const purposeVal = artifact?.purpose ?? metadataLabels?.purpose
			const purposeCustomVal = artifact?.purpose_custom ?? metadataLabels?.purpose_custom
			const timeLabel = getLabelDisplayOrNull(timeDim, t)
			if (timeLabel) metadataBadges.push(timeLabel)
			const purposeLabel =
				purposeVal === 'other_custom' && purposeCustomVal
					? purposeCustomVal
					: getLabelDisplayOrNull(purposeVal, t)
			if (purposeLabel) metadataBadges.push(purposeLabel)
		}

		// Determine styling based on status
		const baseStyles = statusConfig
			? cn(
					'flex justify-between items-center border rounded-lg p-3 transition-all duration-300 ease-in-out cursor-pointer hover:opacity-90',
					statusConfig.bgColor,
					statusConfig.borderColor
				)
			: cn(
					'flex justify-between border border-gray-200 rounded-lg p-3 hover:bg-gray-50 cursor-pointer transition-all duration-300 ease-in-out'
				)

		const handleDownload = async () => {
			setDownloading(true)
			try {
				const response = await getDownloadUrl({
					path: { project_id: projectId, artifact_id: artifactId },
				})
				if (response.error) {
					toast.error(
						extractErrorMessage(response.error, t('pages.projectDetail.artifactPreview.errorDownloading'))
					)
				} else if (response.data?.download_url) {
					window.open(response.data.download_url, '_blank', 'noopener,noreferrer')
				}
			} catch (err) {
				toast.error(extractErrorMessage(err, t('pages.projectDetail.artifactPreview.errorDownloading')))
			} finally {
				setDownloading(false)
			}
		}

		const finishSuccessfulRemove = (usedOptimisticRemove: boolean) => {
			setIsDeleteDialogOpen(false)
			toast.success(t('pages.projectDetail.artifactDeleted'))
			if (usedOptimisticRemove) {
				onOptimisticRemoveComplete?.(artifactId)
			} else if (onDeleted) {
				onDeleted(artifactId)
			}
		}

		const failRemove = (usedOptimisticRemove: boolean, error?: unknown) => {
			if (usedOptimisticRemove && onOptimisticRemoveFailed) {
				onOptimisticRemoveFailed(artifactId)
			}
			if (error !== undefined && isErrorHandledByInterceptor(error)) {
				return
			}
			toast.error(t('pages.projectDetail.artifactDeleteFailed'))
		}

		// If parent passed `onOptimisticRemove`, drop the row before DELETE so in-flight processing
		// (and cancel) updates cannot briefly show a failed state on the tile; otherwise use `onDeleted` on success.
		// In-progress files use bulk remove so processing is cancelled; single DELETE stays rejected while busy.
		const removeArtifact = async () => {
			setIsDeleting(true)
			const usedOptimisticRemove = Boolean(onOptimisticRemove)
			const stopInProgress = isProcessing
			if (stopInProgress) {
				onMarkUploadCancelled?.(artifactId)
			}
			if (usedOptimisticRemove) {
				onOptimisticRemove?.(artifactId)
			}
			try {
				if (stopInProgress) {
					const response = await bulkDeleteArtifacts({
						path: { project_id: projectId },
						body: {
							artifact_ids: [artifactId],
							stop_processing: true,
						},
					})
					const deletedCount = response.data?.total_deleted ?? 0
					const notFoundCount = response.data?.total_not_found ?? 0
					const errorCount = response.data?.total_errors ?? 0
					const removed = errorCount === 0 && (deletedCount > 0 || notFoundCount > 0)
					if (removed) {
						onAbortUploadNetwork?.(artifactId)
						finishSuccessfulRemove(usedOptimisticRemove)
					} else {
						onClearUploadCancelled?.(artifactId)
						failRemove(usedOptimisticRemove)
					}
				} else {
					const response = await deleteArtifact({
						path: {
							project_id: projectId,
							artifact_id: artifactId,
						},
					})

					if (response.data?.success) {
						finishSuccessfulRemove(usedOptimisticRemove)
					} else {
						failRemove(usedOptimisticRemove)
					}
				}
			} catch (error) {
				if (stopInProgress) {
					onClearUploadCancelled?.(artifactId)
				}
				if (!isErrorHandledByInterceptor(error)) {
					captureUnexpectedError(error)
				}
				failRemove(usedOptimisticRemove, error)
			} finally {
				setIsDeleting(false)
			}
		}

		return (
			<div ref={ref} className={cn(baseStyles, className)} {...props}>
				<div className="flex items-center gap-3">
					{statusConfig &&
						(status === 'completed' ? (
							React.createElement(getFileIconForArtifact(artifact ?? undefined, fileType), {
								className: cn('w-5 h-5', statusConfig.color),
							})
						) : statusConfig.icon ? (
							<statusConfig.icon
								className={cn('w-5 h-5', statusConfig.color, isSpinning && 'animate-spin')}
							/>
						) : null)}

					<div className="flex flex-col gap-1.5 min-w-0">
						<h1 className={statusConfig ? 'font-medium' : ''}>{title}</h1>

						{status === 'processing' && statusConfig && (
							<p className={cn('text-xs font-medium', statusConfig.color)}>{statusConfig.text}</p>
						)}

						{(metadataBadges.length > 0 || (showLongProcessingHint && status === 'processing')) && (
							<div className="flex flex-wrap items-center gap-1">
								{metadataBadges.map((label) => (
									<Badge
										key={label}
										variant="secondary"
										className="text-xs font-normal border-purple-200 bg-purple-50 text-purple-900 dark:border-purple-800 dark:bg-purple-950/50 dark:text-purple-100"
									>
										{label}
									</Badge>
								))}
								{showLongProcessingHint && status === 'processing' && (
									<Badge
										variant="outline"
										className="text-xs font-normal border-amber-200 bg-amber-50 text-amber-900 max-w-full whitespace-normal"
									>
										{t('pages.projectDetail.artifactStatus.longProcessing')}
									</Badge>
								)}
							</div>
						)}
					</div>
				</div>

				{showEllipsis && (
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<button
								type="button"
								className="flex items-center justify-center rounded-full p-1 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 hover:cursor-pointer"
								aria-label={t('common.moreOptions')}
							>
								<Ellipsis className="w-5 h-5" />
							</button>
						</DropdownMenuTrigger>

						<DropdownMenuContent side="right" align="start" className="min-w-56">
							{artifact && onEditSuccess && (
								<DropdownMenuItem
									onClick={(e) => {
										e.stopPropagation()
										setIsEditModalOpen(true)
									}}
									className="hover:cursor-pointer"
								>
									<Tag className="size-4 mr-2" aria-hidden />
									{t('pages.projectDetail.artifactPreview.editLabels')}
								</DropdownMenuItem>
							)}
							<DropdownMenuItem
								onClick={(e) => {
									e.stopPropagation()
									handleDownload()
								}}
								disabled={downloading}
								className="hover:cursor-pointer"
							>
								{downloading ? (
									<Spinner className="size-4 mr-2 animate-spin" aria-hidden />
								) : (
									<Download className="size-4 mr-2" aria-hidden />
								)}
								{t('pages.projectDetail.artifactPreview.downloadRawFile')}
							</DropdownMenuItem>
							<DropdownMenuItem
								variant="destructive"
								disabled={!canDeleteArtifact}
								className="hover:cursor-pointer text-[#ff3131] focus:text-[#ff3131]"
								onSelect={(e) => {
									e.stopPropagation()
									if (!canDeleteArtifact) return
									setIsDeleteDialogOpen(true)
								}}
							>
								<Trash2 className="size-4 mr-2" aria-hidden />
								{t('pages.projectDetail.deleteArtifact.deleteSingle')}
							</DropdownMenuItem>
							{onStartBulkDelete && canDeleteByRights && (
								<DropdownMenuItem
									onSelect={(e) => {
										e.stopPropagation()
										onStartBulkDelete(artifactId)
									}}
									className="hover:cursor-pointer"
								>
									<ListChecks className="size-4 mr-2" aria-hidden />
									{t('pages.projectDetail.bulkDelete.selectFiles')}
								</DropdownMenuItem>
							)}
						</DropdownMenuContent>
					</DropdownMenu>
				)}
				{artifact && onEditSuccess && (
					<ArtifactEditModal
						isOpen={isEditModalOpen}
						onClose={() => setIsEditModalOpen(false)}
						artifact={artifact}
						projectId={projectId}
						onSuccess={() => onEditSuccess(artifactId)}
					/>
				)}

				<AlertDialog
					open={isDeleteDialogOpen}
					onOpenChange={(open) => !isDeleting && setIsDeleteDialogOpen(open)}
				>
					<AlertDialogContent>
						<AlertDialogHeader>
							<AlertDialogTitle>{t('pages.projectDetail.deleteArtifact.title')}</AlertDialogTitle>

							<AlertDialogDescription>
								{isProcessing
									? t('pages.projectDetail.deleteArtifact.descriptionInProgress', { title })
									: t('pages.projectDetail.deleteArtifact.description', { title })}
							</AlertDialogDescription>
						</AlertDialogHeader>

						<AlertDialogFooter>
							<AlertDialogCancel
								className="hover:scale-105 active:scale-95 transition-transform duration-200 ease-in-out"
								type="button"
								disabled={isDeleting}
							>
								{t('common.cancel')}
							</AlertDialogCancel>

							<Button
								variant="destructive"
								className="hover:scale-105 active:scale-95 transition-transform duration-200 ease-in-out"
								type="button"
								onClick={removeArtifact}
								disabled={isDeleting}
							>
								{isDeleting ? (
									<>
										<Spinner className="size-4 mr-2" aria-hidden />
										{t('pages.projectDetail.deleteArtifact.deleting')}
									</>
								) : (
									t('pages.projectDetail.deleteArtifact.delete')
								)}
							</Button>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialog>
			</div>
		)
	}
)
ArtifactTile.displayName = 'ArtifactTile'

export { ArtifactTile }
